import { useEffect, useMemo, useRef } from "react";
import { message } from "antd";
import { useQuery } from "@tanstack/react-query";
import { GetOrderById } from "../../Api";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import type { InvoiceTemplateContext } from "../templates";
import { DEFAULT_STALE_TIME } from "../../Constant";

type InvoiceConfig = InvoiceTemplateContext["config"];
type InvoiceOrder = InvoiceTemplateContext["order"];

interface UseInvoiceDataParams {
  open: boolean;
  orderId: string | null;
}

const invoiceQueryKeys = {
  detail: (orderId: string | null) => ["invoice", "detail", orderId] as const,
};

const fetchOrder = async (orderId: string): Promise<InvoiceOrder> => {
  const res = await GetOrderById(orderId);
  if (!res?.success) {
    throw new Error(res?.message || "Failed to load order details.");
  }
  return res.data;
};

export const useInvoiceData = ({ open, orderId }: UseInvoiceDataParams) => {
  const {
    refresh: refreshSystemConfig,
    storeName,
    address,
    phone,
    currency,
    receiptFooter,
    email,
    whatsapp,
    get,
  } = useSystemConfig();
  const prevOpenRef = useRef(open);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      refreshSystemConfig();
    }
    prevOpenRef.current = open;
  }, [open, refreshSystemConfig]);

  const query = useQuery<InvoiceOrder, Error>({
    queryKey: invoiceQueryKeys.detail(orderId),
    enabled: open && Boolean(orderId),
    staleTime: DEFAULT_STALE_TIME,
    queryFn: () => fetchOrder(orderId || ""),
  });

  const config: InvoiceConfig = useMemo(
    () => ({
      store_name: storeName,
      address,
      phone,
      currency,
      receipt_footer: receiptFooter,
      email,
      whatsapp,
      website: (get("website") as string) || "",
      logo_url: (get("app_logo") as string) || "",
    }),
    [storeName, address, phone, currency, receiptFooter, email, whatsapp, get],
  );

  useEffect(() => {
    if (query.error) {
      message.error(query.error.message);
    }
  }, [query.error]);

  return {
    ...query,
    order: query.data ?? null,
    config,
  };
};
